import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';

import {
  alignedLines,
  applyMark,
  rebaseMarks,
  richSegments,
  selectionFormat,
  type RichText,
  type TextFormat,
  type TextMarkStyle,
  type TextSegment,
} from '../lib/rich-text';
import {KEEPS_TEXT_SELECTION, useRichTextSession} from '../lib/rich-text-session';
import {lineCss, markCss} from './renderers/element-styles';

/**
 * A text box whose words can each carry their own style (DERP-307).
 *
 * It replaces the `<textarea>` the canvas used to edit a header and a table
 * cell in, because a textarea holds one style for all of its text. It is a
 * `contentEditable`, but the browser never decides what is in it: every
 * keystroke is read back as plain text, the marks are carried onto it
 * (`rebaseMarks`), and the box is redrawn from that model with the caret put
 * back. So what the author sees while typing is what the renderer draws after,
 * and the browser's own `<b>` / `<div>` markup never reaches a template.
 *
 * The browser's undo cannot survive that redraw, so the box keeps its own.
 *
 * Once a line carries its own alignment each line is a block, and a browser
 * left to edit blocks does so in ways of its own: deleting across two merges
 * them, and Chrome treats an empty line's lone `\n` as a placeholder that the
 * first letter typed there replaces. So typing and deleting are made on the
 * model instead — see the `beforeinput` handler — and only composition (IME
 * input) is left to the browser, which `onInput` reads back as before.
 */

export interface RichTextEditorHandle {
  /** Put the caret at `offset`, clamped to the text. */
  setCaret: (offset: number) => void;
}

type Range = {start: number; end: number};

/** The plain-text offset of a DOM point inside `root`. */
function offsetOf(root: HTMLElement, node: Node, offset: number): number {
  const r = document.createRange();
  r.selectNodeContents(root);
  r.setEnd(node, offset);
  return r.toString().length;
}

function readSelection(root: HTMLElement): Range | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const r = sel.getRangeAt(0);
  if (!root.contains(r.startContainer) || !root.contains(r.endContainer)) return null;
  return {
    start: offsetOf(root, r.startContainer, r.startOffset),
    end: offsetOf(root, r.endContainer, r.endOffset),
  };
}

/** Marks the empty line a text ending in `\n` ends with, when lines are blocks. */
const EMPTY_LINE = 'data-empty-line';

/**
 * The DOM point at a plain-text offset. An offset just after a `\n` is the start
 * of the next line, so it lands at the start of the next run rather than at the
 * end of the one the `\n` closes — which, with lines as blocks, is still the
 * line above.
 */
function pointAt(root: HTMLElement, offset: number): [Node, number] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let at = 0;
  let last: Text | null = null;
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const t = n as Text;
    const end = at + t.length;
    if (offset < end || (offset === end && !t.data.endsWith('\n'))) return [t, offset - at];
    at = end;
    last = t;
  }
  const tail = root.lastElementChild;
  if (tail?.hasAttribute(EMPTY_LINE)) return [tail, 0];
  return last ? [last, last.length] : [root, 0];
}

function writeSelection(root: HTMLElement, {start, end}: Range) {
  const sel = window.getSelection();
  if (!sel) return;
  const r = document.createRange();
  const [sn, so] = pointAt(root, start);
  const [en, eo] = pointAt(root, end);
  r.setStart(sn, so);
  r.setEnd(en, eo);
  sel.removeAllRanges();
  sel.addRange(r);
}

function runs(segments: TextSegment[]): HTMLElement[] {
  return segments.map((seg) => {
    const span = document.createElement('span');
    Object.assign(span.style, markCss(seg.style));
    span.textContent = seg.text;
    return span;
  });
}

/**
 * Draw `value` into `root`: one span per run, nothing the browser invented —
 * and, once a line carries its own alignment, one block per line, as
 * `RichTextView` draws it.
 */
function paint(root: HTMLElement, value: RichText) {
  const lines = alignedLines(value);
  if (!lines) {
    const nodes: Node[] = runs(richSegments(value));
    // `pre-wrap` draws no line after a trailing newline without something on it.
    if (value.text.endsWith('\n')) nodes.push(document.createElement('br'));
    root.replaceChildren(...nodes);
    return;
  }
  root.replaceChildren(
    ...lines.map((line) => {
      const block = document.createElement('span');
      Object.assign(block.style, lineCss(line.align));
      const kids = runs(line.segments);
      if (kids.length > 0) {
        block.replaceChildren(...kids);
      } else {
        block.setAttribute(EMPTY_LINE, '');
        block.replaceChildren(document.createElement('br'));
      }
      return block;
    }),
  );
}

const isMod = (e: React.KeyboardEvent) => e.metaKey || e.ctrlKey;
/** Typing within this long of the last change joins the same undo step. */
const UNDO_COALESCE_MS = 600;

export const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  {
    value: RichText;
    onChange: (value: RichText) => void;
    /** The element being edited — see `RichTextSession.owner`. */
    owner: string;
    /** What the text inherits from its element — see `selectionFormat`. */
    base: TextFormat;
    /** Focus left the editor and the toolbar: the edit is over. */
    onBlurAway: () => void;
    /** The caller's own keys — Enter, Escape, Tab — after the editor's. */
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
    className?: string;
    style?: React.CSSProperties;
  }
>(function RichTextEditor(
  {value, onChange, owner, base, onBlurAway, onKeyDown, className, style},
  ref,
) {
  const rootRef = useRef<HTMLDivElement>(null);
  /** The model as last drawn — the prop can lag a keystroke behind. */
  const current = useRef(value);
  /** The selection as last seen inside the box, kept while focus is in the toolbar. */
  const saved = useRef<Range>({start: value.text.length, end: value.text.length});
  const composing = useRef(false);
  const history = useRef<{past: RichText[]; future: RichText[]; at: number}>({
    past: [],
    future: [],
    at: 0,
  });
  /** Focus is on its way back from the toolbar — see `applyImpl`. */
  const refocusing = useRef(false);
  /** Stops watching the toolbar for focus leaving it — see `onBlur`. */
  const stopWatching = useRef<(() => void) | null>(null);
  const propsRef = useRef({onChange, onBlurAway, owner, base});
  propsRef.current = {onChange, onBlurAway, owner, base};

  /**
   * Format the saved selection. Rebuilt every render so it reads the latest
   * props; the session is handed `stableApply`, which never changes identity,
   * so a toolbar that captured it still reaches this.
   */
  const applyImpl = useRef<(patch: TextMarkStyle) => void>(() => {});
  const stableApply = useRef((patch: TextMarkStyle) => applyImpl.current(patch)).current;

  const publish = useCallback(() => {
    const {start, end} = saved.current;
    const {setSession, clearSession} = useRichTextSession.getState();
    if (start === end) {
      clearSession(propsRef.current.owner, stableApply);
      return;
    }
    setSession({
      owner: propsRef.current.owner,
      format: selectionFormat(current.current, start, end, propsRef.current.base),
      apply: stableApply,
    });
  }, [stableApply]);

  /** Make `next` the model, redraw, and put the selection at `sel`. */
  const commit = useCallback(
    (next: RichText, sel: Range, {coalesce = false, refocus = true} = {}) => {
      const h = history.current;
      const now = Date.now();
      if (!(coalesce && now - h.at < UNDO_COALESCE_MS)) h.past.push(current.current);
      if (h.past.length > 200) h.past.shift();
      h.future = [];
      h.at = coalesce ? now : 0;
      current.current = next;
      saved.current = sel;
      const root = rootRef.current;
      if (root) {
        paint(root, next);
        if (refocus) {
          root.focus({preventScroll: true});
          writeSelection(root, sel);
        }
      }
      propsRef.current.onChange(next);
      publish();
    },
    [publish],
  );

  applyImpl.current = (patch) => {
    const {start, end} = saved.current;
    if (start === end) return;
    const v = current.current;
    commit({text: v.text, marks: applyMark(v, start, end, patch)}, {start, end}, {refocus: false});
    // A colour well keeps focus while it is in use: pulling it back to the text
    // would close the picker under the author's pointer. Anything else hands
    // focus straight back, with the words still selected.
    const active = document.activeElement;
    if (active instanceof HTMLInputElement && active.type === 'color') return;
    // A tick later, not now. The size field applies on Enter by blurring, and
    // the browser carries out that same Enter after the handler returns — on
    // whatever has focus by then. Focused now, the box received it and the
    // selected words were replaced by a line break.
    refocusing.current = true;
    setTimeout(() => {
      refocusing.current = false;
      const root = rootRef.current;
      if (!root) return;
      root.focus({preventScroll: true});
      writeSelection(root, saved.current);
    });
  };

  // Mount: draw, focus, and put the caret at the end. A layout effect declared
  // before the handle, so the text is drawn before a caller's ref can ask for
  // the caret somewhere else.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    paint(root, current.current);
    root.focus({preventScroll: true});
    const len = current.current.text.length;
    saved.current = {start: len, end: len};
    writeSelection(root, saved.current);
  }, []);

  useImperativeHandle(ref, () => ({
    setCaret: (offset) => {
      const root = rootRef.current;
      if (!root) return;
      const at = Math.max(0, Math.min(offset, current.current.text.length));
      saved.current = {start: at, end: at};
      writeSelection(root, saved.current);
    },
  }));

  // A value from outside is redrawn; the editor's own echo is the same object
  // and is not.
  useEffect(() => {
    if (value === current.current) return;
    current.current = value;
    const root = rootRef.current;
    if (!root) return;
    paint(root, value);
    const len = value.text.length;
    saved.current = {
      start: Math.min(saved.current.start, len),
      end: Math.min(saved.current.end, len),
    };
    if (document.activeElement === root) writeSelection(root, saved.current);
  }, [value]);

  // Follow the selection while it is inside the box; close the session on unmount.
  useEffect(() => {
    const onSelection = () => {
      const root = rootRef.current;
      if (!root || document.activeElement !== root) return;
      const sel = readSelection(root);
      if (!sel) return;
      saved.current = sel;
      publish();
    };
    document.addEventListener('selectionchange', onSelection);
    return () => {
      document.removeEventListener('selectionchange', onSelection);
      stopWatching.current?.();
      useRichTextSession.getState().clearSession(propsRef.current.owner, stableApply);
    };
  }, [publish, stableApply]);

  /** Replace [start, end) of the model with `insert`, caret after it. */
  const replaceRange = useCallback(
    ({start, end}: Range, insert: string, coalesce = false) => {
      const v = current.current;
      const text = v.text.slice(0, start) + insert + v.text.slice(end);
      const at = start + insert.length;
      commit({text, marks: rebaseMarks(v.text, v.marks, text)}, {start: at, end: at}, {coalesce});
    },
    [commit],
  );

  const replaceSelection = (insert: string) => {
    const root = rootRef.current;
    replaceRange((root && readSelection(root)) ?? saved.current, insert);
  };

  // Typing and deleting edit the model — see the component comment. The browser
  // still says WHAT a key deletes (a character, a word, a grapheme cluster),
  // through the event's target range; only applying it is ours. Composition
  // stays the browser's.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onBeforeInput = (e: InputEvent) => {
      if (e.isComposing || e.inputType.includes('Composition')) return;
      const sel = readSelection(root);
      if (!sel) return;
      if (e.inputType.startsWith('delete')) {
        const target = e.getTargetRanges()[0];
        if (!target && sel.start === sel.end) return;
        e.preventDefault();
        const range = target
          ? {
              start: offsetOf(root, target.startContainer, target.startOffset),
              end: offsetOf(root, target.endContainer, target.endOffset),
            }
          : sel;
        if (range.start !== range.end) replaceRange(range, '', true);
        return;
      }
      if (e.inputType === 'insertText') {
        e.preventDefault();
        replaceRange(sel, e.data ?? '', true);
      }
    };
    root.addEventListener('beforeinput', onBeforeInput);
    return () => root.removeEventListener('beforeinput', onBeforeInput);
  }, [replaceRange]);

  const toggle = (key: 'bold' | 'italic' | 'underline') => {
    const {start, end} = saved.current;
    if (start === end) return;
    const on = selectionFormat(current.current, start, end, propsRef.current.base)[key];
    stableApply({[key]: !on});
  };

  const undo = (redo: boolean) => {
    const h = history.current;
    const from = redo ? h.future : h.past;
    const to = redo ? h.past : h.future;
    const next = from.pop();
    if (!next) return;
    to.push(current.current);
    h.at = 0;
    current.current = next;
    const root = rootRef.current;
    const len = next.text.length;
    saved.current = {start: len, end: len};
    if (root) {
      paint(root, next);
      writeSelection(root, saved.current);
    }
    propsRef.current.onChange(next);
    publish();
  };

  return (
    <div
      ref={rootRef}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline
      spellCheck={false}
      className={className}
      style={{
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        outline: 'none',
        minHeight: '1.25em',
        ...style,
      }}
      onInput={() => {
        const root = rootRef.current;
        if (!root || composing.current) return;
        const text = root.textContent ?? '';
        const sel = readSelection(root) ?? {start: text.length, end: text.length};
        const v = current.current;
        commit({text, marks: rebaseMarks(v.text, v.marks, text)}, sel, {coalesce: true});
      }}
      onCompositionStart={() => {
        composing.current = true;
      }}
      onCompositionEnd={() => {
        composing.current = false;
        rootRef.current?.dispatchEvent(new Event('input', {bubbles: true}));
      }}
      onPaste={(e) => {
        e.preventDefault();
        replaceSelection(e.clipboardData.getData('text/plain').replace(/\r\n?/g, '\n'));
      }}
      onDrop={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        e.stopPropagation();
        const k = e.key.toLowerCase();
        if (isMod(e) && (k === 'b' || k === 'i' || k === 'u')) {
          e.preventDefault();
          toggle(k === 'b' ? 'bold' : k === 'i' ? 'italic' : 'underline');
          return;
        }
        if (isMod(e) && (k === 'z' || k === 'y')) {
          e.preventDefault();
          undo(k === 'y' || e.shiftKey);
          return;
        }
        if (e.key === 'Enter' && e.shiftKey) {
          e.preventDefault();
          replaceSelection('\n');
          return;
        }
        onKeyDown?.(e);
      }}
      onBlur={(e) => {
        const to = e.relatedTarget as HTMLElement | null;
        const keeper = to?.closest(`[${KEEPS_TEXT_SELECTION}]`);
        if (!keeper) {
          propsRef.current.onBlurAway();
          return;
        }
        // Focus went to the toolbar. The edit ends only once it leaves there for
        // somewhere that is neither the toolbar nor this box — read a tick
        // later, after the control's own blur has applied what it holds.
        const onOut = () => {
          setTimeout(() => {
            // Coming back: the box's own focus stops the watch.
            if (refocusing.current) return;
            const now = document.activeElement;
            if (now === rootRef.current) return;
            if (now?.closest(`[${KEEPS_TEXT_SELECTION}]`)) return;
            stopWatching.current?.();
            propsRef.current.onBlurAway();
          });
        };
        stopWatching.current?.();
        keeper.addEventListener('focusout', onOut);
        stopWatching.current = () => {
          keeper.removeEventListener('focusout', onOut);
          stopWatching.current = null;
        };
      }}
      onFocus={() => stopWatching.current?.()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    />
  );
});
